import type { CityConfig } from "../city-template/types";

export const presqueIsleConfig: CityConfig = {
  city: "Presque Isle",
  state: "Maine",
  stateAbbr: "ME",
  slug: "presque-isle",
  route: "/cities/presque-isle",
  title: "Emergency & Fast Passport Renewal Presque Isle ME | Same-Day & Expedited | Fast Passport Center",
  metaDescription: "Emergency passport and expedited passport services in Presque Isle, Maine — as fast as 24 hours. Fast passport renewal, same-day help, and child passport expediting for Presque Isle, Caribou, Fort Fairfield, and Aroostook County travelers. A+ BBB rated, registered State Dept. courier.",
  h1: "Emergency & Expedited Passport Services in Presque Isle, ME",
  heroSubtitle: "Passport expired before your trip? Need an emergency passport fast? We help Presque Isle and northern Maine travelers get their expedited passports as quickly as 24 hours. A+ BBB rated. No office visit required.",
  heroImageUrl: "https://readdy.ai/api/search-image?query=Presque%20Isle%20Maine%20northern%20Maine%20rural%20town%20with%20rolling%20potato%20fields%2C%20quiet%20Main%20Street%20with%20historic%20buildings%2C%20vast%20open%20sky%20and%20rural%20landscape%2C%20beautiful%20New%20England%20countryside%20with%20warm%20afternoon%20light%2C%20professional%20photography%2C%20scenic%20small%20town%20with%20surrounding%20farmland%2C%20no%20visible%20text%20or%20people%2C%20clean%20rural%20northern%20Maine%20scenery&width=1800&height=700&seq=presque-isle-me-hero-001&orientation=landscape",
  heroImageAlt: "Presque Isle Maine rural northern town with rolling farmland and open sky",
  geoPosition: "46.6812;-67.9961",
  geoPlacename: "Presque Isle, Maine",
  lastModified: "2026-06-15",
  regionalAgency: {
    name: "Boston Passport Agency",
    address: "5 Custom House St, Boston, MA 02110",
    distance: "Located in Boston — approximately 350 miles (6 hours) from Presque Isle, ME",
  },
  nearbyCitiesZips: [
    { name: "Downtown Presque Isle", zips: "04769" },
    { name: "Caribou", zips: "04736" },
    { name: "Fort Fairfield", zips: "04742" },
    { name: "Limestone", zips: "04750" },
    { name: "Washburn", zips: "04786" },
    { name: "Mars Hill", zips: "04758" },
    { name: "Easton", zips: "04740" },
    { name: "Mapleton", zips: "04757" },
    { name: "Ashland", zips: "04732" },
    { name: "Houlton", zips: "04730" },
    { name: "Van Buren", zips: "04785" },
    { name: "Madawaska", zips: "04756" },
  ],
  benefits: [
    { icon: "ri-time-line", title: "24–48 Hour Processing", desc: "Emergency courier submission for urgent Presque Isle departures" },
    { icon: "ri-shield-check-line", title: "Registered Courier", desc: "U.S. Department of State approved passport expediting" },
    { icon: "ri-user-line", title: "Dedicated Agent", desc: "A real person assigned to your case from start to finish" },
    { icon: "ri-mail-send-line", title: "Document Shipping", desc: "Prepaid overnight labels to our processing center" },
  ],
  internalLinks: [
    { name: "Fast passport renewal in Bangor", route: "/cities/bangor" },
    { name: "Emergency passport help in Portland", route: "/cities/portland-me" },
    { name: "Urgent passport services in Augusta", route: "/cities/augusta-me" },
    { name: "Same-day passport help in Waterville", route: "/cities/waterville" },
    { name: "Expedited passport services in Lewiston", route: "/cities/lewiston" },
    { name: "Passport courier services in Houlton", route: "/houlton" },
    { name: "Rush passport help in Caribou", route: "/caribou" },
  ],
  fpcOfficeNote: "Fast Passport Center serves Presque Isle, Maine remotely via secure courier. We do not have a walk-in office in Presque Isle — our registered couriers hand-deliver your documents directly to U.S. Department of State processing facilities. This is how we achieve 24–48 hour turnaround for Presque Isle travelers.",
  fpcOfficeDistance: "Serving all of Aroostook County and northern Maine",
  serviceAreaIntro: "Fast Passport Center provides emergency and fast passport renewal services throughout Presque Isle and all of Aroostook County — the northernmost county in Maine. From downtown Presque Isle to the potato fields of Fort Fairfield and the border towns of Madawaska — we help northern Maine travelers get their passports fast, often in 24–48 hours through our registered courier service.",
  nearbyCitiesIntro: "We serve all of northern Maine including Presque Isle, Caribou, Fort Fairfield, Limestone, Washburn, Mars Hill, Easton, Mapleton, Ashland, and Houlton. We also serve travelers in the St. John Valley including Van Buren and Madawaska.",
  howItWorksIntro: "Presque Isle travelers can complete everything online and ship documents overnight. No need to make the long 6-hour drive to Boston. Our emergency and expedited passport process is designed for northern Maine travelers who need fast, reliable results without the trip to the regional agency.",
  mistakesIntro: "Northern Maine residents often feel far from passport services. The good news is our courier service works from anywhere — you just need to avoid these common mistakes.",
  testimonialsIntro: "Presque Isle travelers trust us when their departure is days away. Here is what some of them have to say.",
  faqIntro: "Have questions about expedited passports in Presque Isle? Here are the most common ones we hear from northern Maine travelers.",
  statePageRoute: "/states/maine",
  statePageName: "Maine",
  stickyNavLinks: [
    { label: "Processing", href: "#processing-options" },
    { label: "Services", href: "#services" },
    { label: "Service Area", href: "#service-area" },
    { label: "Facilities", href: "#acceptance-facilities" },
    { label: "Comparison", href: "#comparison" },
    { label: "How It Works", href: "#how-it-works" },
    { label: "FAQ", href: "#faq" },
    { label: "Resources", href: "#local-resources" },
  ],
  processingOptions: [
    {
      speed: "24",
      unit: "Hours*",
      label: "Regional Agency Processing",
      tag: "Emergency Only",
      tagColor: "bg-gray-100 text-gray-500",
      features: [
        "Appointment required — no walk-ins allowed",
        "Life-or-death emergencies only",
        "Bereavement or medical crisis",
        "Expect to wait all day or return next day",
      ],
      highlight: false,
      emergency: true,
    },
    {
      speed: "2–9",
      unit: "Business Days",
      label: "Fast Passport Center",
      tag: "Most Popular",
      tagColor: "bg-[#F6C344] text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Predictable turnaround",
        "Ideal for upcoming trips",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: true,
    },
    {
      speed: "3–5",
      unit: "Weeks",
      label: "Fast Passport Center",
      tag: "Standard Expedited",
      tagColor: "bg-[#1A365D]/10 text-[#1A365D]",
      features: [
        "Registered U.S. Department of State Courier",
        "Reserved agency submission",
        "Faster than self-service",
        "Good for planned travel",
        "Document review included",
        "Dedicated agent assigned to your case",
        "Daily status updates — never left guessing",
        "Not affected by government shutdowns",
      ],
      highlight: false,
      smartSaver: true,
    },
    {
      speed: "1–3",
      unit: "Months",
      label: "Self-Service (Post Office)",
      tag: "Slowest",
      tagColor: "bg-gray-100 text-gray-400",
      features: [
        "No courier access — standard mail only",
        "Workload & wait times shift with shutdowns and budget fights",
        "In-person clerk appointment required",
        "No document review — errors cause rejection",
        "No dedicated agent — you are on your own",
        "Updates only by physical mail letter",
        "No phone or email contact for your case",
        "Can't upgrade speed after submission",
        "Not suitable if travel is within 10 weeks",
      ],
      highlight: false,
      muted: true,
    },
  ],
  services: [
    {
      icon: "ri-flashlight-line",
      title: "Emergency Passport",
      description: "For travelers with immediate departure needs — as fast as 24 hours for urgent Presque Isle international flights.",
      bullets: ["24–48 hour turnaround available", "Registered courier hand-delivery", "Flight verification support"],
      badge: "Most Popular",
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-passport-line",
      title: "Passport Renewal",
      description: "Fast renewal for adults with a passport issued within the last 15 years at age 16 or older.",
      bullets: ["No clerk visit required for most", "Mail-in or courier options", "Lost passport replacement available"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=passport-renewal",
    },
    {
      icon: "ri-add-circle-line",
      title: "New Passport",
      description: "First-time passport application guidance with acceptance clerk appointment support.",
      bullets: ["Acceptance facility locator", "Form DS-11 assistance", "Photo requirement guidance"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=new-passport",
    },
    {
      icon: "ri-alert-line",
      title: "Lost or Stolen Passport",
      description: "Passport replacement with DS-64 form guidance and police report documentation.",
      bullets: ["DS-64 form assistance", "Identity verification support", "Emergency replacement options"],
      badge: "Urgent",
      link: "https://fastpassportcenter.com/checkout/?passport=lost-stolen-passport",
    },
    {
      icon: "ri-parent-line",
      title: "Child Passport",
      description: "Passport services for minors under 16 with parental consent and acceptance clerk support.",
      bullets: ["DS-11 form for minors", "Parental consent guidance", "Both parents presence rules"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=child-passport",
    },
    {
      icon: "ri-file-edit-line",
      title: "Name Change",
      description: "Passport correction and name change service after marriage, divorce, or legal changes.",
      bullets: ["DS-5504 or DS-82 guidance", "Marriage certificate support", "Court order documentation"],
      badge: null,
      link: "https://fastpassportcenter.com/checkout/?passport=name-change",
    },
  ],
  servicesSchema: [
    { name: "Emergency Passport", description: "Urgent passport processing for travelers with immediate departure needs, as fast as 24 hours." },
    { name: "Passport Renewal", description: "Fast passport renewal for U.S. citizens with a passport issued within the last 15 years at age 16 or older." },
    { name: "New Passport", description: "First-time passport application assistance with step-by-step guidance through the acceptance clerk process." },
    { name: "Lost or Stolen Passport", description: "Passport replacement service for lost or stolen passports, including DS-64 form guidance." },
    { name: "Child Passport", description: "Passport services for minors under 16, with parental consent guidance and acceptance clerk support." },
    { name: "Name Change", description: "Passport correction and name change service after marriage, divorce, or legal name changes." },
  ],
  testimonials: [
    {
      name: "James H.",
      location: "Presque Isle, ME",
      rating: 5,
      text: "Thought being in northern Maine meant I'd have to drive 6 hours to Boston. Fast Passport Center proved me wrong — shipped my documents from Presque Isle and had my passport in 48 hours. No road trip required.",
      service: "Emergency Passport",
    },
    {
      name: "Linda G.",
      location: "Caribou, ME",
      rating: 5,
      text: "My husband and I needed passports for our cruise to Bermuda. Fast Passport Center processed both renewals in under a week. They made it easy even from northern Maine.",
      service: "Passport Renewal",
    },
    {
      name: "Robert P.",
      location: "Fort Fairfield, ME",
      rating: 5,
      text: "Lost my passport two days before my flight to Ireland. I was panicking. Fast Passport Center handled the DS-64 and got me a replacement in 24 hours. Saved my trip and my sanity.",
      service: "Lost Passport",
    },
    {
      name: "Marie C.",
      location: "Presque Isle, ME",
      rating: 5,
      text: "I live near the Canadian border and travel frequently for family visits. Fast Passport Center has been my go-to for expedited renewals. Reliable service that works no matter how far north you live.",
      service: "Expedited Renewal",
    },
  ],
  facilities: [
    {
      id: "presque-isle-1",
      name: "Presque Isle Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "23 North St",
      city: "Presque Isle",
      state: "ME",
      zip: "04769",
      phone: "(207) 764-0311",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "0.5 miles from downtown Presque Isle",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Acceptance hours are limited. Appointments required. Call ahead to confirm passport acceptance times.",
      icon: "ri-mail-line",
    },
    {
      id: "presque-isle-2",
      name: "Caribou Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "24 Sweden St",
      city: "Caribou",
      state: "ME",
      zip: "04736",
      phone: "(207) 493-3741",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "15 miles from Presque Isle",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Convenient for Caribou residents. Limited passport hours — call to confirm.",
      icon: "ri-mail-line",
    },
    {
      id: "presque-isle-3",
      name: "Fort Fairfield Town Office",
      type: "Town Office Passport Acceptance",
      address: "18 Community Center Dr",
      city: "Fort Fairfield",
      state: "ME",
      zip: "04742",
      phone: "(207) 473-7852",
      hours: "Mon–Fri: 8:00 AM – 4:00 PM",
      distance: "20 miles from Presque Isle",
      website: "https://www.fortfairfield.org/",
      websiteLabel: "Fort Fairfield Town Office",
      notes: "Good option for Fort Fairfield and Easton residents. Appointment recommended.",
      icon: "ri-government-line",
    },
    {
      id: "presque-isle-4",
      name: "Houlton Post Office",
      type: "USPS Passport Acceptance Facility",
      address: "40 Court St",
      city: "Houlton",
      state: "ME",
      zip: "04730",
      phone: "(207) 532-2151",
      hours: "Mon–Fri: 9:00 AM – 4:00 PM",
      distance: "45 miles from Presque Isle",
      website: "https://tools.usps.com/find-location.htm",
      websiteLabel: "USPS Location Finder",
      notes: "Popular for southern Aroostook County. Appointment required.",
      icon: "ri-mail-line",
    },
    {
      id: "presque-isle-5",
      name: "Limestone Town Office",
      type: "Town Office Passport Acceptance",
      address: "93 Main St",
      city: "Limestone",
      state: "ME",
      zip: "04750",
      phone: "(207) 325-4261",
      hours: "Mon–Fri: 8:00 AM – 4:00 PM",
      distance: "8 miles from Presque Isle",
      website: "https://www.limestonemaine.org/",
      websiteLabel: "Limestone Town Office",
      notes: "Small town office with limited hours. Call ahead to schedule passport acceptance.",
      icon: "ri-government-line",
    },
    {
      id: "presque-isle-6",
      name: "Washburn Town Office",
      type: "Town Office Passport Acceptance",
      address: "85 Main St",
      city: "Washburn",
      state: "ME",
      zip: "04786",
      phone: "(207) 455-4513",
      hours: "Mon–Fri: 8:00 AM – 4:00 PM",
      distance: "12 miles from Presque Isle",
      website: "https://www.washburnmaine.com/",
      websiteLabel: "Washburn Town Office",
      notes: "Convenient for northern Aroostook County residents. Call to confirm.",
      icon: "ri-government-line",
    },
  ],
  comparisonRows: [
    { feature: "Appointment Needed", agency: "Yes — call NPIC, limited slots", fpc: "No — we handle everything for you" },
    { feature: "Processing Speed", agency: "Same-day if approved (rare)", fpc: "24–48 hours guaranteed for emergency" },
    { feature: "Document Review", agency: "No — rejected if wrong", fpc: "Yes — we check before submission" },
    { feature: "Travel to Agency", agency: "Yes — drive to Boston", fpc: "No — ship from home or office" },
    { feature: "Agent Support", agency: "No — government clerk only", fpc: "Yes — dedicated agent assigned" },
    { feature: "Status Updates", agency: "No — no tracking provided", fpc: "Yes — daily email updates" },
    { feature: "Guaranteed Timing", agency: "No — subject to availability", fpc: "Yes — contractual turnaround times" },
  ],
  mistakes: [
    {
      icon: "ri-image-line",
      title: "Wrong Photo Size or Background",
      description: "Maine photo shops often use generic sizes. Passport photos must be exactly 2×2 inches with a white or off-white background.",
    },
    {
      icon: "ri-file-list-line",
      title: "Incomplete DS-11 Form",
      description: "Missing signatures or using the wrong form type are the #1 reasons for rejection at Presque Isle acceptance facilities.",
    },
    {
      icon: "ri-money-dollar-circle-line",
      title: "Wrong Payment Amount",
      description: "Fees change frequently. Many Presque Isle applicants show up with incorrect checks and have to reschedule their appointment.",
    },
    {
      icon: "ri-calendar-close-line",
      title: "No Proof of Travel",
      description: "Without flight confirmation or travel itinerary, the Boston regional agency will turn you away for emergency appointments.",
    },
    {
      icon: "ri-parent-line",
      title: "Missing Parental Consent",
      description: "For minors, both parents must appear or submit notarized consent. This trips up many Presque Isle families at the clerk.",
    },
    {
      icon: "ri-time-line",
      title: "Waiting Too Long to Start",
      description: "Presque Isle travelers often assume standard processing will be fine. It takes 8–11 weeks. Start expedited at least 3 weeks before travel.",
    },
  ],
  steps: [
    { number: "1", icon: "ri-file-list-line", title: "Apply Online", description: "Complete your application on our secure website in under 10 minutes." },
    { number: "2", icon: "ri-mail-send-line", title: "Ship Documents", description: "We send you a prepaid overnight label. Drop your documents at any UPS location in Presque Isle." },
    { number: "3", icon: "ri-shield-check-line", title: "We Review", description: "Our team checks every document for accuracy before hand-delivering to the State Department." },
    { number: "4", icon: "ri-passport-line", title: "Processing", description: "Your passport is processed at a U.S. Department of State facility using our registered courier slots." },
    { number: "5", icon: "ri-truck-line", title: "Delivery", description: "We overnight your new passport back to your Presque Isle address — trackable every step of the way." },
  ],
  faqs: [
    {
      q: "How fast can I get an emergency passport in Presque Isle, Maine?",
      a: "Fast Passport Center offers emergency passport processing in Presque Isle in as little as 24–48 hours. Our registered couriers hand-deliver your documents directly to U.S. Department of State facilities, bypassing standard mail delays. This is the fastest reliable option for Presque Isle travelers with urgent international flights.",
    },
    {
      q: "Can I get a same-day passport in Presque Isle, Maine?",
      a: "Same-day passports are only available at the Boston Regional Passport Agency for documented life-or-death emergencies with proof of travel within 72 hours. For all other urgent travel, Fast Passport Center's 24–48 hour emergency courier processing is the fastest guaranteed option available to Presque Isle residents.",
    },
    {
      q: "Where can I get a fast passport renewal in Presque Isle, Maine?",
      a: "Fast Passport Center offers fast passport renewal in Presque Isle with no office visit required. We serve all of Aroostook County and northern Maine. Simply complete your application online, ship your documents using our prepaid overnight label, and our couriers handle the rest.",
    },
    {
      q: "What is the fastest way to get an urgent passport in Presque Isle, Maine?",
      a: "The fastest way to get an urgent passport in Presque Isle is through Fast Passport Center's emergency courier service. Our registered couriers deliver your documents directly to State Department processing facilities, achieving 24–48 hour turnaround. This is significantly faster than standard mail processing or even government expedited mail.",
    },
    {
      q: "Do I need to visit the Boston Passport Agency?",
      a: "No. With Fast Passport Center, you complete everything online and ship your documents overnight. Our couriers handle the in-person delivery to State Department facilities. You only need to visit a local acceptance clerk if you're a first-time applicant or need a name change.",
    },
    {
      q: "Where are the closest passport acceptance facilities in Presque Isle?",
      a: "Popular options include the Presque Isle Post Office (04769), Caribou Post Office (04736), Fort Fairfield Town Office (04742), and Limestone Town Office (04750). All require appointments. We help you find the most convenient location and schedule your visit.",
    },
    {
      q: "What if I live in Caribou, Fort Fairfield, or Washburn?",
      a: "We serve all of Aroostook County including Caribou, Fort Fairfield, Washburn, Limestone, Mars Hill, Easton, and Houlton. Our overnight shipping labels work from any UPS location, so your exact location in northern Maine doesn't affect processing speed.",
    },
    {
      q: "How much does expedited passport service cost in Presque Isle?",
      a: "Government fees are fixed: $130 for a passport book (adult) plus a $60 expedite fee. Fast Passport Center's service fees vary by processing speed. Contact us for a personalized quote based on your timeline and needs.",
    },
    {
      q: "Do you help with passport photos in Presque Isle?",
      a: "We guide you on exact photo requirements and can recommend locations near you. Many CVS and Walgreens locations in Presque Isle and Caribou offer passport photo services. We review your photos before submission to ensure they meet State Department standards.",
    },
    {
      q: "What documents do I need for a first-time passport in Presque Isle?",
      a: "You'll need proof of citizenship (birth certificate or naturalization certificate), government-issued photo ID (driver's license or REAL ID), two passport photos, and payment. We provide a complete checklist and review your documents before you visit the acceptance clerk.",
    },
    {
      q: "Can you help with a child's passport in Presque Isle?",
      a: "Yes. Child passports require both parents (or notarized consent from the absent parent), the child's birth certificate, and both parents' IDs. We walk Presque Isle families through the entire process and help schedule acceptance clerk appointments.",
    },
    {
      q: "How far is Presque Isle from the Boston Passport Agency?",
      a: "Presque Isle is approximately 350 miles from the Boston Passport Agency — about a 6-hour drive. With Fast Passport Center, you avoid that long trip entirely. We courier your documents directly to State Department facilities, saving you the full day of driving.",
    },
  ],
  photoLocations: [
    { name: "CVS Pharmacy — Presque Isle", address: "781 Main St, Presque Isle, ME 04769", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Presque Isle", address: "139 Main St, Presque Isle, ME 04769", hours: "Mon–Sun: 7AM–10PM" },
    { name: "UPS Store — Presque Isle", address: "23 North St, Presque Isle, ME 04769", hours: "Mon–Fri: 8AM–7PM, Sat: 9AM–5PM" },
    { name: "Walmart — Presque Isle", address: "830 Main St, Presque Isle, ME 04769", hours: "Mon–Sun: 6AM–11PM" },
    { name: "CVS Pharmacy — Caribou", address: "16 Sweden St, Caribou, ME 04736", hours: "Mon–Sun: 8AM–10PM" },
    { name: "Walgreens — Houlton", address: "15 North St, Houlton, ME 04730", hours: "Mon–Sun: 7AM–10PM" },
    { name: "Walmart — Caribou", address: "30 Access Hwy, Caribou, ME 04736", hours: "Mon–Sun: 6AM–11PM" },
    { name: "Rite Aid — Presque Isle", address: "23 North St, Presque Isle, ME 04769", hours: "Mon–Sun: 8AM–10PM" },
  ],
  dmvLocations: [
    {
      name: "BMV — Presque Isle",
      address: "23 North St, Presque Isle, ME 04769",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "REAL ID and driver's license renewals. Appointment required.",
      featured: true,
    },
    {
      name: "BMV — Caribou",
      address: "24 Sweden St, Caribou, ME 04736",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Limited services. Online appointment booking recommended.",
      featured: false,
    },
    {
      name: "BMV — Houlton",
      address: "40 Court St, Houlton, ME 04730",
      hours: "Mon–Fri: 8AM–4:30PM",
      note: "Full-service center for southern Aroostook County.",
      featured: false,
    },
    {
      name: "BMV — Fort Fairfield",
      address: "18 Community Center Dr, Fort Fairfield, ME 04742",
      hours: "Mon–Fri: 8AM–4:00PM",
      note: "Basic services available. Call ahead.",
      featured: false,
    },
  ],
  airports: [
    {
      code: "BGR",
      name: "Bangor International Airport",
      distance: "165 miles from Presque Isle (2.5 hours)",
      address: "2870 Godfrey Blvd, Bangor, ME 04401",
      note: "Maine's second busiest airport. The closest major airport for Presque Isle travelers with domestic flights and seasonal international connections.",
      flightUrl: "https://www.flybangor.com/",
      flightLabel: "Bangor Airport",
      featured: true,
    },
    {
      code: "PWM",
      name: "Portland International Jetport",
      distance: "280 miles from Presque Isle (4.5 hours)",
      address: "1001 Westbrook St, Portland, ME 04102",
      note: "Maine's busiest airport with direct flights to Canada, Florida, and major U.S. hubs. A long drive from Presque Isle but an option for international connections.",
      flightUrl: "https://www.portlandjetport.org/",
      flightLabel: "PWM Flight Info",
      featured: false,
    },
    {
      code: "PQI",
      name: "Presque Isle International Airport",
      distance: "2 miles from downtown (5 minutes)",
      address: "650 Airport Dr, Presque Isle, ME 04769",
      note: "Small regional airport with limited commercial flights. United Express service to Washington Dulles (IAD). Useful for connecting to international flights.",
      flightUrl: "https://www.presqueislemaine.gov/",
      flightLabel: "Presque Isle Airport",
      featured: true,
    },
  ],
};